import React, { useState, useRef, useEffect } from 'react';
import './App.css';

const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

function App() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showForm, setShowForm] = useState(false);

  return (
    <div className="app-wrapper">
          <header className="site-header">

        <div className="header-content">

          <img src="arch.svg" alt="Arch Point Icon" className="header-logo" />

          <div className="brand-container">

            <span className="brand-name">ARCH POINT SOLUTIONS</span>

            <span className="tagline">Agentic AI: Anchored in Security. Aligned for Action.</span>

          </div>

        </div>

      </header>

      <main className="container">
        <section className="hero-section">
          <h1>Anchoring Authority in an Agentic World</h1>
          <p>
            Arch Point Solutions provides the governance infrastructure for high-stakes Agentic Workflows. 
            <br/>
            We ensure every agentic action is anchored to human intent through: <br/> 
            robust safety, reliability, and forensic integrity.          
          </p>

          {!showForm && !isSubmitted && (
            <div className="submit-container" style={{ marginTop: '2rem' }}>
              <button onClick={() => setShowForm(true)} className="submit-btn">
                Reserve a Governance Audit
              </button>
            </div>
          )}
        </section>

        {/* This wrapper stays in the DOM, but its height/opacity is toggled */}
        <div className={`form-transition-wrapper ${showForm || isSubmitted ? 'is-visible' : ''}`}>
          <section className="form-card">
            {isSubmitted ? (
              <div className="success-message">
                <div className="success-icon">✓</div>
                <h2>Thank You!</h2>
                <p>Your request for a Governance Audit has been received.</p>
                <button 
                  onClick={() => {
                    setIsSubmitted(false);
                    setShowForm(false);
                  }} 
                  className="outline-btn"
                >
                  Send Another Request
                </button>
              </div>
            ) : (
              <LeadForm onFormSuccess={() => setIsSubmitted(true)} />
            )}
          </section>
        </div>
      </main>

      {/* ... Footer stays the same ... */}
    </div>
  );
}

function LeadForm({ onFormSuccess }) {
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState("");
  
  const formRef = useRef(null);
  const turnstileWidgetRef = useRef(null);
  const tokenRef = useRef("");

  useEffect(() => {
    if (window.turnstile && turnstileWidgetRef.current) {
      turnstileWidgetRef.current.innerHTML = "";
      window.turnstile.render(turnstileWidgetRef.current, {
        sitekey: turnstileSiteKey,
        size: "invisible",
        callback: (token) => {
          tokenRef.current = token;
        },
      });
    }
  }, []);

  const formatPhoneNumber = (value) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, '');
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formattedValue = formatPhoneNumber(e.target.value);
    setPhone(formattedValue);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    if (window.turnstile && turnstileWidgetRef.current) {
      if (tokenRef.current) {
        handleSubmitWithToken();
      } else {
        window.turnstile.execute(turnstileWidgetRef.current);
      }
    } else {
      alert("Security check not loaded. Please refresh the page.");
      setLoading(false);
    }
  };

  const handleSubmitWithToken = async () => {
    const formData = new FormData(formRef.current);
    formData.append('cf-turnstile-response', tokenRef.current);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://archpointsolutionsaddlead.ghotir.workers.dev', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        onFormSuccess();
      } else {
        const errorText = await response.text();
        alert("Error: " + errorText);
      }
    } catch (err) {
      alert("Network error: " + err.message);
    } finally {
      setLoading(false);
      tokenRef.current = "";
    }
  };

  return (
    <div className="fade-in">
      <form onSubmit={handleSubmit} id="leadForm" ref={formRef}>
        <div className="radio-group">
          <label className="radio-option">
            <input 
              type="radio" 
              name="interest" 
              value="Assessment" 
              required 
            />
            <div className="radio-text">
              <span><strong>Assessment:</strong> We need to audit our AI risks and build a Governance Roadmap.</span>
            </div>
          </label>
          <label className="radio-option">
            <input 
              type="radio" 
              name="interest" 
              value="Safety Anchor" 
             />
            <div className="radio-text">
              <span><strong>Safety Anchor: </strong>We are deploying agents and need SpanLock™ for a Pilot station.</span>
            </div>
          </label>
          <label className="radio-option">
            <input 
              type="radio" 
              name="interest" 
              value="Scale" 
             />
            <div className="radio-text">
              <span><strong>Scale: </strong>We want to discuss an Enterprise-wide Sovereignty rollout.</span>
            </div>
          </label>
        </div>

        <div className="input-row">
          <div className="form-group">
            <label>*First Name</label>
            <input type="text" name="firstName" required />
          </div>
          <div className="form-group">
            <label>*Last Name</label>
            <input type="text" name="lastName" required />
          </div>
        </div>

        <div className="input-row">
          <div className="form-group">
            <label>*Title</label>
            <input type="text" name="title" required />
          </div>
          <div className="form-group">
            <label>*Company</label>
            <input type="text" name="company" required />
          </div>
        </div>

        <div className="form-group">
          <label>*Work Email</label>
          <input type="email" name="email" required />
        </div>

        <div className="form-group">
          <label>Phone Number</label>
          <input 
            type="tel" 
            name="phoneNumber" 
            value={phone}
            onChange={handlePhoneChange}
          />
        </div>

        <div ref={turnstileWidgetRef} className="cf-turnstile"></div>
        <div className="submit-container">
          <button type="submit" disabled={loading} className="submit-btn">
            {loading ? "Processing..." : "Reserve a Governance Audit"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default App;